import { useEffect, useState } from 'react'
import { Alert, Button, Card, Descriptions, Form, Input, Skeleton, Space, Tag, Tooltip, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  PORTAL_TEMPLATE_RULES,
  fetchPortalTemplate,
  updatePortalTemplate,
  type PortalTemplate,
} from './portalTemplateApi'

interface PortalTemplatePanelProps {
  venueId: string
}

interface FormValues {
  logoUrl?: string
  primaryColor?: string
  welcomeUz?: string
  welcomeRu?: string
  welcomeEn?: string
  termsUrl?: string
  supportPhone?: string
}

/** Bo'sh maydon = TOZALASH (backend `PortalTemplateDraft` javadoc'i) */
function nullable(value: string | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/**
 * Mehmon portalining brend ko'rinishi — logotip, rang, salomlashish,
 * yordam telefoni va shartlar havolasi.
 *
 * <h2>Nima UCHUN bu yerda, zona panelida emas</h2>
 * Ko'rinish OB'EKT darajasida: bitta mehmonxonaning lobbisi va nomerlari
 * (ikki alohida zona) bir xil brendni ko'rsatishi kerak. Identifikatsiya
 * talablari esa aksincha, zonaga xos — shuning uchun ular
 * `VenueZonesPanel` da qoladi va bu yerda UMUMAN yo'q. Ularni birlashtirish
 * "rang tanlash" huquqini "identifikatsiyani o'chirish" huquqiga
 * aylantirardi (№137 qarori).
 *
 * <h2>⚠️ Bo'sh maydon — o'chirish</h2>
 * Har saqlashda TO'LIQ shakl yuboriladi. Ya'ni maydonni tozalab saqlash
 * qiymatni O'CHIRADI. Bu ataylab: aks holda qo'yilgan logotipni paneldan
 * olib tashlab bo'lmasdi, faqat boshqasiga almashtirish mumkin bo'lardi.
 */
export function PortalTemplatePanel({ venueId }: PortalTemplatePanelProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: queryKeys.venues.portalTemplate(venueId),
    queryFn: () => fetchPortalTemplate(venueId),
  })

  const template = query.data

  useEffect(() => {
    if (!editing || !template) return
    form.setFieldsValue({
      logoUrl: template.logoUrl ?? undefined,
      primaryColor: template.primaryColor ?? undefined,
      welcomeUz: template.welcomeUz ?? undefined,
      welcomeRu: template.welcomeRu ?? undefined,
      welcomeEn: template.welcomeEn ?? undefined,
      termsUrl: template.termsUrl ?? undefined,
      supportPhone: template.supportPhone ?? undefined,
    })
  }, [editing, template, form])

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      updatePortalTemplate(venueId, {
        logoUrl: nullable(values.logoUrl),
        primaryColor: nullable(values.primaryColor)?.toLowerCase() ?? null,
        welcomeUz: nullable(values.welcomeUz),
        welcomeRu: nullable(values.welcomeRu),
        welcomeEn: nullable(values.welcomeEn),
        termsUrl: nullable(values.termsUrl),
        supportPhone: nullable(values.supportPhone),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venues.portalTemplate(venueId) })
      setEditing(false)
      setError(null)
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const title = t('portalTemplate.title')

  if (query.isPending) {
    return (
      <Card title={title}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    )
  }

  if (query.isError) {
    return (
      <Card title={title}>
        <Alert type="error" showIcon message={translateError(query.error, t)} />
      </Card>
    )
  }

  const current = template as PortalTemplate

  return (
    <Card
      title={title}
      extra={
        editing ? null : (
          <Can permission="portal.template.update">
            <Button onClick={() => setEditing(true)}>{t('common.change')}</Button>
          </Can>
        )
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        {t('portalTemplate.hint')}
      </Typography.Paragraph>

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {!editing ? (
        <>
          {!current.configured ? (
            // Bo'sh holat — "boshi berk ko'cha" bo'lmasin: huquqi bor
            // odamga darhol nima qilish kerakligi aytiladi, huquqi
            // yo'qqa esa nima uchun bo'shligi.
            <Alert
              type="info"
              showIcon
              message={t('portalTemplate.emptyTitle')}
              description={
                can('portal.template.update')
                  ? t('portalTemplate.emptyHint')
                  : t('portalTemplate.emptyNoPermission')
              }
              style={{ marginBottom: 16 }}
            />
          ) : null}

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('portalTemplate.logoUrl')}>
              {current.logoUrl ? (
                <Typography.Link href={current.logoUrl} target="_blank" rel="noopener noreferrer">
                  {current.logoUrl}
                </Typography.Link>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('portalTemplate.primaryColor')}>
              {current.primaryColor ? (
                <Space>
                  {/* Namuna kvadrati — rang kodi o'z-o'zicha hech narsa aytmaydi */}
                  <span
                    aria-hidden
                    style={{
                      display: 'inline-block',
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      background: current.primaryColor,
                      border: '1px solid rgba(127,127,127,.4)',
                      verticalAlign: 'middle',
                    }}
                  />
                  <Typography.Text code>{current.primaryColor}</Typography.Text>
                </Space>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('portalTemplate.welcomeUz')}>
              {current.welcomeUz ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('portalTemplate.welcomeRu')}>
              {current.welcomeRu ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('portalTemplate.welcomeEn')}>
              {current.welcomeEn ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('portalTemplate.termsUrl')}>
              {current.termsUrl ? (
                <Typography.Link href={current.termsUrl} target="_blank" rel="noopener noreferrer">
                  {current.termsUrl}
                </Typography.Link>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('portalTemplate.supportPhone')}>
              {current.supportPhone ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('portalTemplate.updatedAt')}>
              {current.updatedAt ? (
                <Tooltip title={utcTooltip(current.updatedAt)}>
                  {formatDateTime(current.updatedAt)}
                </Tooltip>
              ) : (
                '—'
              )}
            </Descriptions.Item>
          </Descriptions>
        </>
      ) : (
        <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
          <Form.Item
            name="logoUrl"
            label={t('portalTemplate.logoUrl')}
            extra={t('portalTemplate.urlHint')}
            rules={[
              { max: PORTAL_TEMPLATE_RULES.urlMax },
              {
                pattern: PORTAL_TEMPLATE_RULES.httpsUrl,
                message: t('portalTemplate.httpsRequired'),
              },
            ]}
          >
            <Input placeholder="https://cdn.example.uz/logo.png" autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="primaryColor"
            label={t('portalTemplate.primaryColor')}
            extra={t('portalTemplate.colorHint')}
            rules={[
              { pattern: PORTAL_TEMPLATE_RULES.hexColor, message: t('portalTemplate.colorFormat') },
            ]}
          >
            <Input placeholder="#2563eb" autoComplete="off" />
          </Form.Item>

          {(['welcomeUz', 'welcomeRu', 'welcomeEn'] as const).map((field) => (
            <Form.Item
              key={field}
              name={field}
              label={t(`portalTemplate.${field}`)}
              rules={[
                { max: PORTAL_TEMPLATE_RULES.welcomeMax },
                {
                  pattern: PORTAL_TEMPLATE_RULES.noMarkup,
                  message: t('portalTemplate.noMarkup'),
                },
              ]}
            >
              <Input.TextArea rows={2} maxLength={PORTAL_TEMPLATE_RULES.welcomeMax} showCount />
            </Form.Item>
          ))}

          <Form.Item
            name="termsUrl"
            label={t('portalTemplate.termsUrl')}
            extra={t('portalTemplate.urlHint')}
            rules={[
              { max: PORTAL_TEMPLATE_RULES.urlMax },
              {
                pattern: PORTAL_TEMPLATE_RULES.httpsUrl,
                message: t('portalTemplate.httpsRequired'),
              },
            ]}
          >
            <Input placeholder="https://example.uz/terms" autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="supportPhone"
            label={t('portalTemplate.supportPhone')}
            rules={[
              { max: PORTAL_TEMPLATE_RULES.phoneMax },
              { pattern: PORTAL_TEMPLATE_RULES.noMarkup, message: t('portalTemplate.noMarkup') },
            ]}
          >
            <Input autoComplete="off" inputMode="tel" />
          </Form.Item>

          <Space>
            <Button
              type="primary"
              loading={save.isPending}
              onClick={() => submitOnValid(form, (values) => save.mutate(values))}
            >
              {t('common.save')}
            </Button>
            <Button
              disabled={save.isPending}
              onClick={() => {
                setEditing(false)
                setError(null)
              }}
            >
              {t('common.cancel')}
            </Button>
            <Tag color="default">{t('portalTemplate.emptyClears')}</Tag>
          </Space>
        </Form>
      )}
    </Card>
  )
}
